import React, { useMemo, useState } from "react";
import {
  isChatAccessDisabledProfile,
  isChatAdminIdentity,
  isClownProfile,
  isComputerProfile,
  isHelperModeratorProfile,
  isModeratorProfile,
  isProfileTimedOut,
  tsMs,
  xpLevelInfoForProfile,
  xpPointsForProfile,
  xpRewardForProfile,
} from "./chatService";
import { avatarColor, avatarInitial } from "./avatar";

const TIMEOUT_PRESET_MINUTES = [5, 15, 60, 240, 1440];

export default function ModeratorPanelModal({
  meUid,
  viewerIsAdmin = false,
  profilesByUid,
  conversations = [],
  onClose,
  onSetMemberRole,
  onSetClownRole,
  onSetChatBan,
  onTimeoutUser,
  onClearTimeout,
  onRemoveComputerPermanently,
  onKickFromGroup,
  onDeleteAccountPermanently,
}) {
  const [busyUid, setBusyUid] = useState("");
  const [error, setError] = useState("");
  // uid -> selected group conversation id, used for the per-user "Kick from group" picker.
  const [selectedGroupByUid, setSelectedGroupByUid] = useState({});
  // uid -> selected timeout duration (minutes, as string) for the per-user timeout picker.
  const [timeoutMinutesByUid, setTimeoutMinutesByUid] = useState({});

  const users = useMemo(() => {
    return Object.values(profilesByUid || {})
      .filter((profile) => profile.uid && profile.uid !== meUid)
      .sort((a, b) => String(a.displayName || "").localeCompare(String(b.displayName || "")));
  }, [meUid, profilesByUid]);

  // Map uid -> list of group conversations they're a member of (admin sees only
  // groups admin is also in, since that's what the listener returns).
  const groupsByUid = useMemo(() => {
    const map = {};
    (conversations || []).forEach((conversation) => {
      if (!conversation || conversation.type !== "group") return;
      const memberIds = Array.isArray(conversation.memberIds) ? conversation.memberIds : [];
      memberIds.forEach((uid) => {
        if (!uid || uid === meUid) return;
        if (!map[uid]) map[uid] = [];
        map[uid].push(conversation);
      });
    });
    return map;
  }, [conversations, meUid]);

  function groupLabel(conversation) {
    if (!conversation) return "";
    if (typeof conversation.name === "string" && conversation.name.trim()) {
      return conversation.name.trim();
    }
    const otherIds = (conversation.memberIds || []).filter((uid) => uid !== meUid);
    const sample = otherIds.slice(0, 3)
      .map((uid) => profilesByUid?.[uid]?.displayName || "User")
      .join(", ");
    const suffix = otherIds.length > 3 ? "…" : "";
    return sample ? `Group: ${sample}${suffix}` : "Group";
  }

  async function setModerationRole(profile, role) {
    if (!profile?.uid) return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onSetMemberRole(profile.uid, role);
    } catch (err) {
      setError(err?.message || "Couldn't update that member role.");
    } finally {
      setBusyUid("");
    }
  }

  async function toggleClown(profile, enabled) {
    if (!profile?.uid) return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onSetClownRole(profile.uid, enabled);
    } catch (err) {
      setError(err?.message || "Couldn't update clown role.");
    } finally {
      setBusyUid("");
    }
  }

  async function toggleBan(profile, disabled) {
    if (!profile?.uid || typeof onSetChatBan !== "function") return;
    if (disabled) {
      const name = profile.displayName || "this user";
      const confirmed = window.confirm(
        `Ban ${name} from the chat?\n\n`
        + "They get kicked from every group, blocked from sending, editing, reacting to, "
        + "or deleting any messages, and every message they ever sent disappears for "
        + "everyone. You can undo this any time with Unban."
      );
      if (!confirmed) return;
    }
    setBusyUid(profile.uid);
    setError("");
    try {
      await onSetChatBan(profile.uid, disabled);
    } catch (err) {
      setError(err?.message || "Couldn't update the ban.");
    } finally {
      setBusyUid("");
    }
  }

  async function applyTimeout(profile) {
    if (!profile?.uid || typeof onTimeoutUser !== "function") return;
    const minutes = Number(timeoutMinutesByUid[profile.uid] || 60);
    if (!Number.isFinite(minutes) || minutes <= 0) return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onTimeoutUser(profile.uid, minutes * 60 * 1000);
    } catch (err) {
      setError(err?.message || "Couldn't apply that timeout.");
    } finally {
      setBusyUid("");
    }
  }

  async function clearTimeoutFor(profile) {
    if (!profile?.uid || typeof onClearTimeout !== "function") return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onClearTimeout(profile.uid);
    } catch (err) {
      setError(err?.message || "Couldn't clear that timeout.");
    } finally {
      setBusyUid("");
    }
  }

  async function removeFromWebsite(profile) {
    if (!profile?.uid || typeof onRemoveComputerPermanently !== "function") return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onRemoveComputerPermanently(profile.uid);
    } catch (err) {
      setError(err?.message || "Couldn't remove the physical device from the website.");
    } finally {
      setBusyUid("");
    }
  }

  async function deleteAccount(profile) {
    if (!profile?.uid || typeof onDeleteAccountPermanently !== "function") return;
    const name = profile.displayName || "this user";
    const confirmed = window.confirm(
      `Permanently delete ${name}'s account?\n\n`
      + "This deletes their login, their profile, their DMs, and removes them from every group. "
      + "This CANNOT be undone."
    );
    if (!confirmed) return;
    setBusyUid(profile.uid);
    setError("");
    try {
      await onDeleteAccountPermanently(profile.uid);
    } catch (err) {
      setError(err?.message || "Couldn't delete that account.");
    } finally {
      setBusyUid("");
    }
  }

  async function kickFromGroup(profile) {
    if (!profile?.uid || typeof onKickFromGroup !== "function") return;
    const groups = groupsByUid[profile.uid] || [];
    if (groups.length === 0) return;
    const selectedId = selectedGroupByUid[profile.uid] || groups[0].id;
    const conversation = groups.find((g) => g.id === selectedId) || groups[0];
    if (!conversation) return;
    if (!window.confirm(`Kick ${profile.displayName || "this user"} from "${groupLabel(conversation)}"?`)) {
      return;
    }
    setBusyUid(profile.uid);
    setError("");
    try {
      await onKickFromGroup(conversation, profile.uid);
    } catch (err) {
      setError(err?.message || "Couldn't kick that user from the group.");
    } finally {
      setBusyUid("");
    }
  }

  return (
    <div className="chat-modal-bg" onClick={busyUid ? undefined : onClose}>
      <div className="chat-modal chat-moderator-modal" onClick={(event) => event.stopPropagation()}>
        <h2>Moderator admin</h2>
        <p className="chat-muted">Manage respected moderator permissions for this server.</p>

        <div className="chat-modal-userlist chat-moderator-list">
          {users.length === 0 ? (
            <div className="chat-muted">No users available.</div>
          ) : (
            users.map((profile) => {
              const isAdmin = isChatAdminIdentity(profile);
              const isHelperModerator = !isAdmin && isHelperModeratorProfile(profile);
              const isModerator = !isAdmin && isModeratorProfile(profile);
              const isClown = !isAdmin && isClownProfile(profile);
              const xpLevel = xpLevelInfoForProfile(profile);
              const xpReward = xpRewardForProfile(profile);
              const isComputer = !isAdmin && isComputerProfile(profile);
              const userGroups = groupsByUid[profile.uid] || [];
              const canKickFromGroup = typeof onKickFromGroup === "function" && userGroups.length > 0;
              const canRemoveFromWebsite = isComputer && typeof onRemoveComputerPermanently === "function";
              const isChatDisabled = isChatAccessDisabledProfile(profile);
              const isTimedOut = isProfileTimedOut(profile);
              const role = isHelperModerator
                ? "helper_mod"
                : isModerator
                  ? "moderator"
                  : "member";
              const roleLabel = isAdmin
                ? "Admin Vivaan"
                : isHelperModerator
                  ? "Helper mod"
                  : isModerator
                    ? "Respected moderator"
                    : "Member";
                const statusLabel = isChatDisabled
                  ? (isTimedOut
                      ? `Banned • timed out until ${formatTimeoutUntil(profile.timeoutUntil)}`
                      : "Banned from chat")
                  : (isTimedOut
                      ? `Timed out until ${formatTimeoutUntil(profile.timeoutUntil)}`
                      : "");
              return (
                <div key={profile.uid} className="chat-moderator-row">
                  <div className="chat-moderator-user">
                    <div className="chat-avatar sm" style={{ background: avatarColor(profile.uid) }}>
                      {avatarInitial(profile.displayName)}
                    </div>
                    <div className="chat-moderator-meta">
                      <strong>{profile.displayName || "User"}</strong>
                        <span>
                          {roleLabel}
                          {isClown ? " • Clown" : ""}
                          {statusLabel ? ` • ${statusLabel}` : ""}
                        </span>
                        <span>
                          {xpPointsForProfile(profile)} XP • Level {xpLevel.level} • {xpReward.label}
                        </span>
                    </div>
                  </div>
                  {!isAdmin && (
                    <div className="chat-moderator-actions">
                      {role !== "moderator" && (
                        <button
                          type="button"
                          className="chat-msg-action-btn"
                          disabled={busyUid === profile.uid}
                          onClick={() => setModerationRole(profile, "moderator")}
                        >
                          Make mod
                        </button>
                      )}
                      {role !== "helper_mod" && (
                        <button
                          type="button"
                          className="chat-msg-action-btn"
                          disabled={busyUid === profile.uid}
                          onClick={() => setModerationRole(profile, "helper_mod")}
                        >
                          Make helper mod
                        </button>
                      )}
                      {role !== "member" && (
                        <button
                          type="button"
                          className="chat-msg-action-btn chat-msg-action-danger"
                          disabled={busyUid === profile.uid}
                          onClick={() => setModerationRole(profile, "member")}
                        >
                          Remove mod role
                        </button>
                      )}
                      <button
                        type="button"
                        className={"chat-msg-action-btn" + (isClown ? " chat-msg-action-danger" : "")}
                        disabled={busyUid === profile.uid}
                        onClick={() => toggleClown(profile, !isClown)}
                      >
                        {isClown ? "Remove clown" : "Make clown"}
                      </button>
                      {viewerIsAdmin && typeof onSetChatBan === "function" && (
                        <button
                          type="button"
                          className={"chat-msg-action-btn" + (isChatDisabled ? "" : " chat-msg-action-danger")}
                          disabled={busyUid === profile.uid}
                          onClick={() => toggleBan(profile, !isChatDisabled)}
                          title={isChatDisabled
                            ? "Unban this person and restore their messages"
                            : "Ban this person from chat and hide every message they sent"}
                        >
                          {isChatDisabled ? "Unban" : "Ban from chat"}
                        </button>
                      )}
                      {typeof onTimeoutUser === "function" && (
                        <span className="chat-moderator-timeout">
                          <select
                            value={timeoutMinutesByUid[profile.uid] || "60"}
                            disabled={busyUid === profile.uid}
                            onChange={(event) =>
                              setTimeoutMinutesByUid((prev) => ({
                                ...prev,
                                [profile.uid]: event.target.value,
                              }))
                            }
                            title="Timeout length"
                          >
                            {TIMEOUT_PRESET_MINUTES.map((minutes) => (
                              <option key={minutes} value={String(minutes)}>
                                {formatDurationLabel(minutes)}
                              </option>
                            ))}
                          </select>
                          <button
                            type="button"
                            className="chat-msg-action-btn"
                            disabled={busyUid === profile.uid}
                            onClick={() => applyTimeout(profile)}
                            title={isTimedOut
                              ? "Apply a new timeout length"
                              : "Timeout this user (works even if they are banned)"}
                          >
                            {isTimedOut ? "Re-timeout" : "Timeout"}
                          </button>
                          {isTimedOut && (
                            <button
                              type="button"
                              className="chat-msg-action-btn"
                              disabled={busyUid === profile.uid}
                              onClick={() => clearTimeoutFor(profile)}
                            >
                              Clear timeout
                            </button>
                          )}
                        </span>
                      )}
                      {typeof onDeleteAccountPermanently === "function" && (
                        <button
                          type="button"
                          className="chat-msg-action-btn chat-msg-action-danger"
                          disabled={busyUid === profile.uid}
                          onClick={() => deleteAccount(profile)}
                          title="Permanently delete this account, including its login and DMs"
                        >
                          Delete account
                        </button>
                      )}
                      {(canKickFromGroup || canRemoveFromWebsite) && (
                        <span className="chat-moderator-kick">
                          {canKickFromGroup && (
                            <>
                              <select
                                value={selectedGroupByUid[profile.uid] || (userGroups[0]?.id || "")}
                                disabled={busyUid === profile.uid}
                                onChange={(event) =>
                                  setSelectedGroupByUid((prev) => ({
                                    ...prev,
                                    [profile.uid]: event.target.value,
                                  }))
                                }
                              >
                                {userGroups.map((conversation) => (
                                  <option key={conversation.id} value={conversation.id}>
                                    {groupLabel(conversation)}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className="chat-msg-action-btn chat-msg-action-danger"
                                disabled={busyUid === profile.uid}
                                onClick={() => kickFromGroup(profile)}
                              >
                                Kick from group
                              </button>
                            </>
                          )}
                          {canRemoveFromWebsite && (
                            <button
                              type="button"
                              className="chat-msg-action-btn chat-msg-action-danger"
                              disabled={busyUid === profile.uid}
                              onClick={() => removeFromWebsite(profile)}
                              title="Permanently remove this physical device from the whole website"
                            >
                              Remove physical device
                            </button>
                          )}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {error && <div className="chat-auth-error">{error}</div>}

        <div className="chat-modal-footer">
          <button type="button" className="chat-btn-link" onClick={onClose} disabled={!!busyUid}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function formatDurationLabel(minutes) {
  if (minutes % 1440 === 0) return `${minutes / 1440}d`;
  if (minutes % 60 === 0) return `${minutes / 60}h`;
  return `${minutes}m`;
}

function formatTimeoutUntil(timeoutUntil) {
  const ms = tsMs(timeoutUntil);
  if (!ms) return "later";
  return new Date(ms).toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
